import React, {useEffect, useRef, useState} from 'react'
import {classNames} from '../wrapper'

// Unique datalist id per mounted instance so rows don't collide.
let instanceId = 0

/**
 * Searchable, nullable select backed by a native <datalist>.
 *
 * Type to filter; pick a suggestion to select. `value`/`onChange` deal in the numeric
 * key (via `valueFunc`), while the visible text comes from `textFunc` — so duplicate
 * display names are fine as long as `textFunc` is unique (e.g. includes an id).
 *
 * The option list is rendered only while the input is focused. That means a page with
 * many AutoComplete rows never renders every option for every row at once — only the
 * focused input pays to render its (potentially large) list.
 */
const AutoComplete = <TOption, >(props: {
    options: TOption[];
    value: number | null;
    onChange: (value: number | null, option: TOption | null) => void;
    textFunc: (option: TOption) => string;
    valueFunc: (option: TOption) => number;
    placeholder?: string;
    className?: string;
    error?: boolean;
}) => {
    const [text, setText] = useState('')
    const [focused, setFocused] = useState(false)
    const listId = useRef('autocomplete-' + instanceId++).current

    // Mirror the selected value into the visible text whenever we are NOT editing.
    // This resolves the label once options load (they can arrive asynchronously) and
    // snaps the text back to the committed value on blur, without fighting the user
    // while they type.
    useEffect(() => {
        if (focused) return
        const found = props.options.find(o => props.valueFunc(o) === props.value)
        setText(found ? props.textFunc(found) : '')
    }, [props.value, props.options, focused])

    return (
        <div className="relative w-full">
            <input
                list={listId}
                value={text}
                placeholder={props.placeholder}
                className={classNames(props.className ?? 'input', props.error ? 'border-error-300' : '')}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={e => {
                    const typed = e.target.value
                    setText(typed)
                    const match = props.options.find(o => props.textFunc(o).trim() === typed.trim())
                    props.onChange(match ? props.valueFunc(match) : null, match ?? null)
                }}
            />
            {focused
                ? <datalist id={listId}>
                    {props.options.map((o, i) => <option key={i} value={props.textFunc(o)}/>)}
                </datalist>
                : null}
        </div>
    )
}

export {AutoComplete}
